import { ANONYMOUS_ID_KEY } from "@/constants/local-storage-keys";

let cached: string | undefined;

/**
 * Stable per-browser id used as Sentry's `user.id` while logged out.
 *
 * Sentry has no built-in anonymous identifier. With no user set it falls back to
 * `user.ip_address`, which is a dead end here on two counts: `sendDefaultPii` is
 * off, so `{{auto}}` is never applied, and events are proxied through
 * `tunnel: "/monitoring"`, so ingest sees our server's egress IP rather than the
 * visitor's. Without this every anonymous error collapses into one bucket and a
 * single bug reads as "30k events, 2 users".
 */
export function getAnonymousId(): string {
  // Never cache on the server: module scope outlives a request there, so one id
  // would be shared by every visitor — the single-bucket bug wearing a uuid.
  // Both current callers are client-only (`Sentry.init` and a `useEffect`); this
  // keeps a future caller from silently poisoning the cache during SSR.
  if (typeof window === "undefined") return crypto.randomUUID();

  if (cached) return cached;

  try {
    const stored = localStorage.getItem(ANONYMOUS_ID_KEY);
    // `||`, not `??`: a stored empty string is corruption, and caching it would
    // send `{ id: "" }` — the single shared bucket this whole module exists to
    // avoid. Every other falsy case is already absent.
    cached = stored || crypto.randomUUID();
    // Doubles as a repair: `!stored` is true for a corrupt empty entry too, so
    // the bad value is overwritten instead of being left to fail again.
    if (!stored) localStorage.setItem(ANONYMOUS_ID_KEY, cached);
  } catch {
    // Partitioned or blocked storage — Petra frames the app, and private mode
    // rejects reads outright. An in-memory id still separates this tab from
    // every other anonymous session, which is the whole point.
    cached ||= crypto.randomUUID();
  }

  return cached;
}
